/**
 * TanStack Query hooks for the credentials admin API.
 *
 * Backend routes: `apps/api/flow_api/routers/credentials.py`. All
 * responses are PHI-free and password-free by contract — only the
 * server (KeyVault path) ever sees raw secret values.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";

export interface Credential {
  id: string;
  entityCode: string;
  portalName: string;
  usernameMasked: string;
  mfaType: "none" | "totp" | "email";
  hasMfaMailbox: boolean;
  lastRotatedAt: string | null;
  rotationDueAt: string | null;
  lastUsedAt: string | null;
  daysToRotation: number | null;
  rotationOverdue: boolean;
}

export interface CredentialsList {
  items: Credential[];
  total: number;
}

export interface CreateCredentialPayload {
  entity_code: string;
  portal_name: string;
  username: string;
  password: string;
  mfa_type: "none" | "totp" | "email";
  totp_secret?: string | null;
  mfa_mailbox?: string | null;
  rotation_days?: number;
}

export interface UpdateCredentialPayload {
  password?: string;
  totp_secret?: string | null;
  mfa_mailbox?: string | null;
  rotation_days?: number;
}

export interface PasswordSuggestion {
  suggested: string;
  note: string;
}

const QK = {
  list: (entity?: string) => ["credentials", "list", entity ?? "all"] as const,
  single: (id: string) => ["credentials", "single", id] as const,
};

export function useCredentials(params?: { entity?: string }) {
  return useQuery<CredentialsList>({
    queryKey: QK.list(params?.entity),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.entity) qs.set("entity", params.entity);
      const tail = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch(`/credentials${tail}`);
    },
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation<Credential, Error, CreateCredentialPayload>({
    mutationFn: (payload) =>
      apiFetch("/credentials", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
    },
  });
}

export function useUpdateCredential(id: string) {
  const qc = useQueryClient();
  return useMutation<Credential, Error, UpdateCredentialPayload>({
    mutationFn: (payload) =>
      apiFetch(`/credentials/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
      qc.invalidateQueries({ queryKey: QK.single(id) });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiFetch(`/credentials/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
    },
  });
}

export function useSuggestPassword(id: string | null) {
  const qc = useQueryClient();
  return useMutation<PasswordSuggestion, Error, void>({
    mutationFn: () => {
      if (!id) throw new Error("no credential id");
      return apiFetch(`/credentials/${id}/rotate-password-suggest`, {
        method: "POST",
        body: JSON.stringify({}),
      });
    },
    // Not caching — each call returns a fresh random suggestion.
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: QK.single(id) });
    },
  });
}
